/*
 * <cd-rail> — a horizontally scrollable strip with optional arrows and dots.
 *
 * The strip itself is a plain overflow-x container with scroll snapping, so it
 * stays usable with touch, trackpad and keyboard even if this script never
 * runs. Arrows and dots are enhancements layered on top.
 *
 * Dots are generated here rather than in Liquid because the number of pages
 * depends on how many cards fit at the current width, which only the browser
 * knows.
 *
 * Wrapped in an IIFE because two sections on one template load this file
 * with their own <script src>, so the browser executes it twice; without the
 * wrapper the second execution throws on the repeated top-level `class`
 * declaration before the customElements.get() guard below ever runs.
 */
(function () {
class CdRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.dots = this.querySelector('[data-rail-dots]');
    this.dotLabel = this.dots?.dataset.label || 'Go to slide';

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));

    this.onScroll = this.raf(() => this.sync());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.observer = new ResizeObserver(this.raf(() => this.build()));
    this.observer.observe(this.track);

    this.build();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /* One "page" is a full viewport of the track. */
  get pageWidth() {
    return this.track.clientWidth;
  }

  get pageCount() {
    if (!this.pageWidth) return 1;
    return Math.max(1, Math.ceil(this.track.scrollWidth / this.pageWidth));
  }

  get pageIndex() {
    if (!this.pageWidth) return 0;
    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
    if (maxScrollLeft - this.track.scrollLeft <= 2) return this.pageCount - 1;
    return Math.round(this.track.scrollLeft / this.pageWidth);
  }

  page(direction) {
    this.track.scrollBy({ left: this.pageWidth * direction, behavior: 'smooth' });
  }

  goTo(index) {
    this.track.scrollTo({ left: this.pageWidth * index, behavior: 'smooth' });
  }

  build() {
    const overflows = this.track.scrollWidth > this.track.clientWidth + 2;
    this.classList.toggle('cd-rail-host--static', !overflows);

    if (this.dots) {
      const wanted = overflows ? this.pageCount : 0;
      if (this.dots.childElementCount !== wanted) {
        this.dots.replaceChildren();
        for (let i = 0; i < wanted; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cd-dot';
          dot.setAttribute('aria-label', `${this.dotLabel} ${i + 1}`);
          dot.addEventListener('click', () => this.goTo(i));
          this.dots.appendChild(dot);
        }
      }
    }

    this.sync();
  }

  sync() {
    const { scrollLeft, clientWidth, scrollWidth } = this.track;
    // 2px of slack absorbs sub-pixel rounding at the ends.
    if (this.prev) this.prev.disabled = scrollLeft <= 2;
    if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 2;

    if (!this.dots) return;
    const active = this.pageIndex;
    Array.from(this.dots.children).forEach((dot, i) => {
      const on = i === active;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  raf(fn) {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn();
      });
    };
  }
}

if (!customElements.get('cd-rail')) {
  customElements.define('cd-rail', CdRail);
}
})();
